/**
 * Request Deduplication Manager
 * 
 * Prevents duplicate simultaneous requests to the same endpoint.
 * If multiple components request the same data at the same time,
 * only ONE request is made and the result is shared with all callers.
 */

class RequestDeduplicationManager {
  constructor() {
    // Store in-flight requests
    this.pendingRequests = new Map();
  }

  /**
   * Execute a request with deduplication
   * @param {string} key - Unique key for this request
   * @param {Function} requestFn - Function that returns a Promise (the actual API call)
   * @returns {Promise} - The request result
   */
  async execute(key, requestFn) {
    // Check if this request is already in-flight
    if (this.pendingRequests.has(key)) {
      console.log(`[Dedup] Reusing in-flight request: ${key}`);
      return this.pendingRequests.get(key);
    }

    // Create new request
    console.log(`[Dedup] Starting new request: ${key}`);
    const requestPromise = requestFn()
      .then((result) => {
        // Remove from pending once completed
        this.pendingRequests.delete(key);
        return result;
      })
      .catch((error) => {
        // Remove from pending on error too
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store the promise
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  /**
   * Clear a specific pending request
   * @param {string} key - Request key to clear
   */
  clear(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * Clear all pending requests
   */
  clearAll() {
    this.pendingRequests.clear();
  }

  /**
   * Get number of pending requests
   */
  getPendingCount() {
    return this.pendingRequests.size;
  }

  /**
   * Check if a request is pending
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }
}

// Create singleton instance
export const requestDeduplication = new RequestDeduplicationManager();

/**
 * Helper function to create deduplication keys
 */
export const createRequestKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});
  
  return `${endpoint}::${JSON.stringify(sortedParams)}`;
};

/**
 * Wrapper for axios requests with automatic deduplication
 * @param {Object} axiosInstance - Axios instance
 * @param {string} method - HTTP method (get, post, etc.)
 * @param {string} url - Request URL
 * @param {Object} config - Axios config
 * @returns {Promise}
 */
export const deduplicatedRequest = (axiosInstance, method, url, config = {}) => {
  const key = createRequestKey(url, { method, ...config.params });
  
  return requestDeduplication.execute(key, () => {
    return axiosInstance[method](url, config);
  });
};

export default requestDeduplication;

